import { IconButton, InputAdornment } from '@mui/material';
import { ChangeEvent, FC, MouseEvent, useState, KeyboardEvent, FocusEvent } from 'react';
import {
  SearchContainer,
  InputField,
  SearchButton,
  SearchIcon,
  AddProductButton
} from './searchbar.styles';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch, useSelector } from 'react-redux';
import { productsActions, productsSelectors } from '../../../store/products';
import { RootState } from '../../../store/store';
import { EditModal } from '../edit-product/edit-modal';
import { singleProductActions } from '../../../store/single-product';
import { v4 as uuidv4 } from 'uuid';
import { useFlag } from '@unleash/proxy-client-react';
import appConfig from '../../../config/app.config';

interface SearchProps {
  onSearch: (event: MouseEvent | KeyboardEvent) => void;
}

export const Searchbar: FC<SearchProps> = ({ onSearch }) => {
  const isAddProductEnabled: boolean = useFlag(appConfig.FEATURE_FLAGS.FEATURES.ADD_PRODUCT);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const searchbarText = useSelector((state: RootState) =>
    productsSelectors.selectSearchbarString(state.products)
  );
  const [focused, setFocused] = useState<boolean>(false);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    dispatch(productsActions.changeSearchbarString(event.target.value));
  }

  function handleSearchClick(event: MouseEvent) {
    dispatch(productsActions.restartSearchFilter());
    onSearch(event);
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && focused) {
      onSearch(event);
    }
  }

  function handleFocus(event: FocusEvent) {
    setFocused((prev) => !prev);
  }

  function handleClearClick(event: MouseEvent) {
    dispatch(productsActions.changeSearchbarString(''));
  }

  function handleAddClick() {
    dispatch(singleProductActions.resetSingleProduct());
    const newUuid = uuidv4();
    dispatch(
      singleProductActions.updateProduct({
        fieldName: 'productId',
        value: newUuid
      })
    );
    setIsEditModalOpen(true);
  }

  function closeEditModal(isUpdateSuccess?: boolean) {
    setIsEditModalOpen(false);
  }

  return (
    <SearchContainer id='search-bar-container'>
      <InputField
        id='search-bar-input'
        type='text'
        value={searchbarText}
        autoComplete='off'
        name='searchString'
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleFocus}
        placeholder={!focused ? 'Search for UPC / Product name' : ''}
        InputProps={{
          startAdornment:
            !focused && searchbarText.length === 0 ? (
              <InputAdornment position={'start'} sx={{ marginRight: '8px' }}>
                <SearchIcon />
              </InputAdornment>
            ) : undefined,
          endAdornment: searchbarText ? (
            <IconButton onClick={handleClearClick}>
              <ClearIcon />
            </IconButton>
          ) : undefined
        }}
      />
      <SearchButton id='search-bar-button' onClick={handleSearchClick}>
        Search
      </SearchButton>
      {isAddProductEnabled && (
        <AddProductButton id='add-product-button' onClick={handleAddClick}>
          Add Product
        </AddProductButton>
      )}
      {isEditModalOpen && (
        <EditModal isOpen={isEditModalOpen} onClose={closeEditModal} isAdd={true} />
      )}
    </SearchContainer>
  );
};
